import { parseDecisionMakers } from "../shared/enums";

const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;

/** Lower-cased, de-duplicated email addresses found in free text. */
export const extractEmails = (text) => {
  const found = (text || "").match(EMAIL_RE) || [];
  return [...new Set(found.map(e => e.toLowerCase().replace(/\.+$/, "")))];
};

const DOT_SEP = /\s*(?:\(dot\)|\[dot\])\s*|\s+\.\s*|\s+(?:dot|punkt)\s+/gi;
const DOMAIN_WITH_SEPS = /@[a-z0-9-]+(?:(?:\.|\s*(?:\(dot\)|\[dot\])\s*|\s+\.\s*|\s+(?:dot|punkt)\s+)[a-z0-9-]+)+/gi;

/**
 * Turns addresses written out by hand or dictated ("sabrina at firma punkt de",
 * "max(at)firma.de", "lisa @ firma .com") into plain ones.
 */
export const normalizeWrittenEmails = (text) => (text || "")
  .replace(/\s*(?:\(at\)|\[at\]|\{at\})\s*/gi, "@")
  .replace(/([a-z0-9._%+-])\s+(?:at|ät)\s+(?=[a-z0-9-]+(?:\.|\s*\(dot\)|\s*\[dot\]|\s+\.|\s+(?:dot|punkt)\s+)[a-z]{2,})/gi, "$1@")
  .replace(/([a-z0-9._%+-])\s*@\s*([a-z0-9])/gi, "$1@$2")
  .replace(DOMAIN_WITH_SEPS, (m) => m.replace(DOT_SEP, "."));

/** Addresses in text, including written-out ones. */
export const extractWrittenEmails = (text) => extractEmails(normalizeWrittenEmails(text));

/** Every address already stored on the contact (general mailbox + people). */
export const knownContactEmails = (contact) => {
  const set = new Set();
  extractEmails(String(contact?.lead_email || "")).forEach(e => set.add(e));
  parseDecisionMakers(contact || {}).forEach(dm => {
    if (dm?.email) extractEmails(dm.email).forEach(e => set.add(e));
  });
  return set;
};

/**
 * Addresses mentioned in the given texts that are not on the contact yet and
 * weren't dismissed. Order follows the texts, so the live composer wins.
 */
export const suggestNewContactEmails = (contact, texts, dismissed = []) => {
  const known = knownContactEmails(contact);
  const skip = new Set(dismissed.map(e => e.toLowerCase()));
  const out = [];
  texts.forEach(t => extractEmails(t).forEach(e => {
    if (!known.has(e) && !skip.has(e) && !out.includes(e)) out.push(e);
  }));
  return out;
};

const GENERIC_LOCAL = /^(info|kontakt|contact|office|mail|hello|hallo|service|support|sales|vertrieb|team|buero|büro|admin|post|anfrage)$/i;

/** Best-guess person name from an address; empty for generic mailboxes. */
export const guessNameFromEmail = (email) => {
  const local = (email || "").split("@")[0];
  if (!local || GENERIC_LOCAL.test(local)) return "";
  return local
    .split(/[._-]+/)
    .filter(p => p && !/^\d+$/.test(p))
    .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(" ");
};
